import {
  Bell,
  Bot,
  Box,
  ChevronDown,
  ChevronRight,
  CircleDot,
  ClipboardCheck,
  GitBranch,
  Hourglass,
  ListTodo,
  Plug,
  Play,
  Rocket,
  Search,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Store,
  Wrench,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  findRecipe,
  type FlowRecipe,
  type SkillInstall,
  type ToolBinding,
} from "@zworks/console-contracts";
import { skillsQuery, toolsQuery } from "@/lib/api";
import { useCurrentProject } from "@/lib/project";
import { cn } from "@/lib/utils";

export type PaletteGroupId =
  "triggers" | "agents" | "skills" | "tools" | "checks" | "evals" | "build" | "probes" | "logic";

export interface NodeTemplate {
  name: string;
  kind: string;
  gate: boolean;
  description: string;
  recipe?: string;
  skill?: string;
  skillRef?: string;
  toolRef?: string;
  bound?: boolean;
  category: string;
  podConfig?: {
    image?: string;
    cpu?: string;
    memory?: string;
    command?: string;
    parallel?: number;
  };
}

interface PaletteGroup {
  id: PaletteGroupId;
  label: string;
  items: NodeTemplate[];
}

const EXECUTOR_DEFAULT_IMAGE: Record<string, string> = {
  auto: "ghcr.io/zworks/planner:latest",
  agent: "ghcr.io/zworks/agent:latest",
  runner: "python:3.11-slim",
};

function recipeTemplate(recipe: FlowRecipe): NodeTemplate {
  return {
    name: recipe.label,
    kind: recipe.id.startsWith("deploy.") ? "deploy" : "task",
    gate: false,
    description: recipe.summary,
    recipe: recipe.id,
    category: recipe.id,
    podConfig: { image: EXECUTOR_DEFAULT_IMAGE[recipe.executor] ?? "python:3.11-slim" },
  };
}

function recipeTemplates(ids: string[]): NodeTemplate[] {
  return ids
    .map((id) => findRecipe(id))
    .filter((recipe): recipe is FlowRecipe => recipe !== null)
    .map(recipeTemplate);
}

function skillTemplate(skill: SkillInstall): NodeTemplate {
  return {
    name: skill.skill,
    kind: "skill",
    gate: false,
    description: `${skill.skill} ${skill.version} · already installed and bound`,
    skill: skill.skill,
    skillRef: skill.id,
    bound: true,
    category: "skills",
  };
}

function toolTemplate(tool: ToolBinding): NodeTemplate {
  return {
    name: tool.tool,
    kind: "tool",
    gate: false,
    description: `${tool.capabilities.join(", ") || tool.tool} · already attached and bound`,
    toolRef: tool.tool,
    bound: true,
    category: "tools",
    podConfig: { image: "node:22-alpine", cpu: "250m", memory: "256Mi", parallel: 1 },
  };
}

const GROUP_ORDER: PaletteGroupId[] = [
  "triggers",
  "agents",
  "skills",
  "tools",
  "checks",
  "evals",
  "build",
  "probes",
  "logic",
];

const GROUP_LABELS: Record<PaletteGroupId, string> = {
  triggers: "Triggers",
  agents: "Agents",
  skills: "Skills",
  tools: "Tools",
  checks: "Checks",
  evals: "Evals and tests",
  build: "Build and release",
  probes: "Probes",
  logic: "Logic",
};

/** Recipes by the sidebar group that lists them first. Duplicates across groups are removed. */
const GROUP_RECIPES: Record<PaletteGroupId, string[]> = {
  triggers: ["auto.plan", "auto.note", "probe.http", "node.test"],
  agents: ["agent.work", "agent.review"],
  skills: [],
  tools: [],
  checks: ["repo.diff", "repo.scan-secrets", "node.install", "node.typecheck", "node.test"],
  evals: ["node.test", "node.audit", "node.licences", "image.smoke"],
  build: [
    "node.build",
    "image.build",
    "image.smoke",
    "image.push",
    "deploy.roll",
    "deploy.rollback",
  ],
  probes: ["probe.http", "probe.headers", "probe.tls", "probe.logs"],
  logic: [],
};

function getTemplateIcon(template: NodeTemplate) {
  if (template.gate) return ShieldAlert;
  if (template.bound && template.skill) return Sparkles;
  if (template.bound && template.toolRef) return Wrench;
  if (template.kind === "deploy" || template.recipe?.startsWith("deploy.")) return Rocket;
  if (template.recipe === "auto.plan") return Play;
  if (template.recipe === "auto.note") return ListTodo;
  if (template.recipe === "agent.work") return Bot;
  if (template.recipe === "agent.review") return ClipboardCheck;
  if (template.recipe?.startsWith("repo.")) return GitBranch;
  if (template.recipe?.startsWith("node.")) return Box;
  if (template.recipe === "image.build" || template.recipe === "image.push") return Rocket;
  if (template.recipe === "probe.logs") return Bell;
  if (template.recipe?.startsWith("probe.") || template.recipe === "image.smoke")
    return ShieldCheck;
  if (template.kind === "note") return CircleDot;
  if (template.kind === "tool") return Wrench;
  if (template.name === "Parallel join") return GitBranch;
  if (template.name === "Wait") return Hourglass;
  if (template.name === "Notify") return Bell;
  if (template.kind === "gate") return ShieldAlert;
  return Box;
}

function serialise(template: NodeTemplate): string {
  return JSON.stringify(template);
}

export function NodePalette({
  canEdit,
  className,
  onAddStep,
}: {
  canEdit: boolean;
  className?: string;
  onAddStep: (template: NodeTemplate) => void;
}) {
  const { project } = useCurrentProject();
  const projectId = project?.id ?? "";
  const skills = useQuery({
    ...skillsQuery(projectId),
    select: (r) => r.list,
    enabled: projectId !== "",
  });
  const tools = useQuery({ ...toolsQuery(projectId), enabled: projectId !== "" });
  const [open, setOpen] = React.useState(true);
  const [query, setQuery] = React.useState("");
  const [collapsed, setCollapsed] = React.useState<Partial<Record<PaletteGroupId, boolean>>>({});

  const groups = React.useMemo<PaletteGroup[]>(() => {
    const skillItems = (skills.data ?? []).map(skillTemplate);
    const toolItems = (tools.data ?? []).map(toolTemplate);
    const seen = new Set<string>();
    return GROUP_ORDER.map((id) => {
      const own = GROUP_RECIPES[id].flatMap((recipeId) => {
        if (seen.has(recipeId)) return [];
        seen.add(recipeId);
        return recipeTemplates([recipeId]);
      });
      const items = id === "skills" ? skillItems : id === "tools" ? toolItems : own;
      return { id, label: GROUP_LABELS[id], items };
    });
  }, [skills.data, tools.data]);

  const needle = query.trim().toLowerCase();
  const visible = React.useMemo(
    () =>
      groups.map((group) => ({
        ...group,
        items: needle
          ? group.items.filter((item) =>
              `${item.name} ${item.description}`.toLowerCase().includes(needle),
            )
          : group.items,
      })),
    [groups, needle],
  );
  const hasResults = visible.some((group) => group.items.length > 0);

  const dragStart = (event: React.DragEvent, template: NodeTemplate) => {
    const data = serialise(template);
    event.dataTransfer.setData("application/json", data);
    event.dataTransfer.setData("text/plain", data);
    event.dataTransfer.effectAllowed = "copy";
  };

  if (!canEdit) return null;

  const toggleGroup = (id: PaletteGroupId) =>
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div
      role="region"
      aria-label="Component palette"
      className={cn(
        "z-20 flex w-64 flex-col overflow-hidden rounded-xl border bg-card/95 shadow-xl backdrop-blur-md border-border/80 select-none",
        className ?? "absolute top-4 left-4",
      )}
    >
      <div className="flex items-center justify-between border-b px-3 py-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold">
          <Sparkles className="size-3.5 text-accent-text" aria-hidden="true" />
          <span>Components</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="size-6 text-muted-foreground hover:text-foreground"
          aria-label={open ? "Collapse components" : "Expand components"}
          onClick={() => setOpen(!open)}
        >
          {open ? (
            <ChevronDown className="size-3.5" aria-hidden="true" />
          ) : (
            <ChevronRight className="size-3.5" aria-hidden="true" />
          )}
        </Button>
      </div>

      {open ? (
        <>
          <div className="border-b px-2 py-2">
            <label className="relative block">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search components"
                aria-label="Search components"
                className="h-8 w-full rounded-md border bg-background pl-8 pr-2 text-xs outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
              />
            </label>
          </div>

          <div className="flex max-h-96 min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-2 text-xs">
            {!hasResults ? (
              <p className="px-1 py-2 text-xs text-muted-foreground">
                No components match this search.
              </p>
            ) : (
              visible.map((group) => {
                const isCollapsed = collapsed[group.id] ?? false;
                return (
                  <section key={group.id} aria-label={group.label}>
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.id)}
                      aria-expanded={!isCollapsed}
                      className="flex w-full items-center justify-between rounded-md px-1 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                    >
                      <span>
                        {group.label}{" "}
                        <span className="tabular-nums text-muted-foreground">
                          {group.items.length}
                        </span>
                      </span>
                      {isCollapsed ? (
                        <ChevronRight className="size-3.5" aria-hidden="true" />
                      ) : (
                        <ChevronDown className="size-3.5" aria-hidden="true" />
                      )}
                    </button>
                    {isCollapsed ? null : group.items.length === 0 ? (
                      <p className="px-1 pb-1 text-xs text-muted-foreground">
                        Nothing installed yet.
                      </p>
                    ) : (
                      <ul className="flex flex-col gap-1.5 pb-1">
                        {group.items.map((item) => {
                          const Icon = getTemplateIcon(item);
                          return (
                            <li
                              key={`${group.id}:${item.name}:${item.recipe ?? item.skill ?? item.toolRef ?? item.kind}`}
                            >
                              <div
                                role="button"
                                tabIndex={0}
                                draggable
                                aria-label={`Add ${item.name}`}
                                onDragStart={(event) => dragStart(event, item)}
                                onClick={() => onAddStep(item)}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    onAddStep(item);
                                  }
                                }}
                                className="group flex cursor-grab active:cursor-grabbing items-start gap-2 rounded-lg border border-border/50 bg-background/80 p-2 transition-all hover:border-primary/50 hover:bg-accent hover:shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                              >
                                <div className="rounded p-1 text-accent-text bg-primary/10 group-hover:bg-primary group-hover:text-primary-foreground transition-colors shrink-0">
                                  <Icon className="size-3.5" aria-hidden="true" />
                                </div>
                                <div className="flex min-w-0 flex-1 flex-col">
                                  <div className="flex items-center justify-between gap-1">
                                    <span className="font-semibold text-foreground truncate">
                                      {item.name}
                                    </span>
                                    {item.gate ? (
                                      <Badge variant="warning" className="text-xs px-1 py-0">
                                        Gate
                                      </Badge>
                                    ) : item.bound ? (
                                      <Badge variant="success" className="text-xs px-1 py-0">
                                        Bound
                                      </Badge>
                                    ) : null}
                                  </div>
                                  <span className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                                    {item.description}
                                  </span>
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </section>
                );
              })
            )}
          </div>

          <div className="flex flex-col gap-1 border-t px-2 py-2 text-xs">
            <Button asChild variant="ghost" size="sm" className="h-7 justify-start text-xs">
              <Link to="/marketplace">
                <Store className="size-3.5" aria-hidden="true" />
                <span>Marketplace</span>
              </Link>
            </Button>
            <Button asChild variant="ghost" size="sm" className="h-7 justify-start text-xs">
              <Link to="/integrations">
                <Plug className="size-3.5" aria-hidden="true" />
                <span>Integrations</span>
              </Link>
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}
